import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { type Event, type SeedAssignment } from '@clubspark-node/clubspark-tennis-types';
import { deepEqual, shallowEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { drawDefinitionConstants, scaleConstants, tournamentEngine } from 'tods-competition-factory';

import { tournamentAtom } from 'atoms/tournament';
import { emptyObject } from 'components/utils';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import useMemoCompare from 'hooks/useMemoCompare';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { getSeedingScaleName, getSeeds } from 'utils/seeds';

const { SEEDING } = scaleConstants;
const { MAIN, QUALIFYING } = drawDefinitionConstants;

interface UseSeedsOptions {
  event: Event;
  entries: ParticipantInterface[];
  seedAssignments: SeedAssignment[];
  flightDrawId?: string;
  isQualifying?: boolean;
  isGetSeeds?: boolean;
}

const useSeeds = (options: UseSeedsOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const memoisedOptions = useMemoCompare(options, shallowEqual);
  const { event, entries, isQualifying, seedAssignments, flightDrawId, isGetSeeds = true } = memoisedOptions;
  const seeds = useMemo(
    () => (isGetSeeds ? getSeeds(event, entries, seedAssignments, flightDrawId, isQualifying) : []),
    [isGetSeeds, event, entries, seedAssignments, flightDrawId, isQualifying],
  );

  const getEventSeedingPublishTimeItem = useCallback(
    () =>
      tournamentEngine.getPublishState({ eventId: event?.eventId })?.publishState?.status?.publishedSeeding ??
      emptyObject,
    [event],
  );
  const publishTimeItem = useAtomValue(selectAtom(tournamentAtom, getEventSeedingPublishTimeItem, deepEqual));

  const isSeedingPublished = isQualifying
    ? !!publishTimeItem?.stageSeedingScaleNames?.QUALIFYING
    : !!publishTimeItem?.stageSeedingScaleNames?.MAIN;

  const [saveSeeds] = useMutateTournament('saveSeeds');
  const [clearSeeds] = useMutateTournament('clearSeeds');
  const [publishSeeding] = useMutateTournament('publishSeeding');
  const [unpublishSeeding] = useMutateTournament('unpublishSeeding');

  const scaleName = getSeedingScaleName(event.eventId, flightDrawId, isQualifying);

  const manualSeedRefs = React.useRef<Record<string, React.MutableRefObject<HTMLInputElement>>>({});
  const manualSeedRefDirtyStates = React.useRef<Record<string, boolean>>(
    Object.fromEntries(seeds.map((seed) => [seed.participantId, false])),
  );

  const createManualSeedRefs = useCallback(
    () =>
      Object.fromEntries(
        seeds.map((seed) => [
          seed.participantId,
          (el: HTMLInputElement) => {
            if (el) {
              if (!manualSeedRefs.current[seed.participantId])
                manualSeedRefs.current[seed.participantId] = React.createRef();
              el.value = seed?.scaleItem?.scaleValue ?? '';
              manualSeedRefs.current[seed.participantId].current = el;
              manualSeedRefDirtyStates.current[seed.participantId] = false;
            }
          },
        ]),
      ),
    [seeds],
  );
  const manualSeedCallbackRefs = React.useRef<Record<string, React.Ref<HTMLInputElement>>>(createManualSeedRefs());

  useEffect(() => {
    manualSeedRefs.current = {};
    manualSeedRefDirtyStates.current = Object.fromEntries(seeds.map((seed) => [seed.participantId, false]));
    manualSeedCallbackRefs.current = createManualSeedRefs();
  }, [createManualSeedRefs, seeds]);

  const save = useCallback(async () => {
    setIsLoading(true);
    const updates = Object.entries(manualSeedRefs.current)
      .filter(([participantId]) => !!manualSeedRefDirtyStates.current[participantId])
      .map(([participantId, ref]) => {
        const seedValue = ref?.current?.value ?? '';

        const scaleItem = {
          scaleType: SEEDING,
          scaleName,
          eventType: event?.eventType,
          scaleValue: seedValue ?? '',
        };

        return {
          participantId,
          scaleItem,
        };
      });

    if (updates.length) {
      await saveSeeds(updates, event?.eventId);
    }

    manualSeedRefDirtyStates.current = Object.fromEntries(
      Object.entries(manualSeedRefs.current).map(([participantId]) => [participantId, false]),
    );
    setIsLoading(false);
  }, [event?.eventType, event?.eventId, saveSeeds, scaleName]);

  const update = useCallback((participantId, seedValue) => {
    const ref = manualSeedRefs.current?.[participantId] ?? React.createRef();
    if (ref) {
      ref.current.value = seedValue ?? '';
      manualSeedRefs.current[participantId] = ref;
      manualSeedRefDirtyStates.current[participantId] = true;
    }
  }, []);

  const publish = useCallback(async () => {
    setIsLoading(true);
    const stageSeedingScaleNames = isQualifying ? { QUALIFYING: scaleName } : { MAIN: scaleName };
    await publishSeeding(event?.eventId, stageSeedingScaleNames);
    setIsLoading(false);
  }, [event?.eventId, isQualifying, publishSeeding, scaleName]);

  const unpublish = useCallback(async () => {
    setIsLoading(true);
    const unpublishMain = !!publishTimeItem?.stageSeedingScaleNames?.MAIN;
    const unpublishQualifying = !!publishTimeItem?.stageSeedingScaleNames?.QUALIFYING;
    const stage = isQualifying && unpublishQualifying ? QUALIFYING : unpublishMain ? MAIN : null;
    await unpublishSeeding(event?.eventId, stage);
    setIsLoading(false);
  }, [
    event?.eventId,
    isQualifying,
    publishTimeItem?.stageSeedingScaleNames?.MAIN,
    publishTimeItem?.stageSeedingScaleNames?.QUALIFYING,
    unpublishSeeding,
  ]);

  const clear = useCallback(
    async (disableToastMessage = false) => {
      setIsLoading(true);
      await clearSeeds(event?.eventId, disableToastMessage, scaleName);
      setIsLoading(false);
    },
    [clearSeeds, event?.eventId, scaleName],
  );

  return {
    inputRefs: manualSeedCallbackRefs,
    isLoading,
    isSeedingPublished,
    seeds,
    clear,
    publish,
    save,
    unpublish,
    update,
  };
};

export default useSeeds;
